import React, { useContext, useState } from "react";
import i18next from "i18next";
import {
  Grid,
  Box,
} from "@mui/material";
import { Context } from "../../../../states/Store";
import EADialog from "../../../../components/common/EADialog";
import { SUCCESS, ERROR } from "../../../../services/common/Constants";
import { createRule, evaluateRule } from "../../../../services/edge/RuleService";
import RuleForm from "../form/RuleForm";
import RuleConfiguration from "../steps/RuleConfiguration";
import RuleSummary from "../steps/RuleSummary";
import RuleFinalization from "../steps/RuleFinalization";
import { useRequestLoading } from "../../../../components/common/hooks/useRequestLoading";
import RuleAction from "../steps/RuleAction";
import { RuleFormSteps } from "services/common/RulesUtils";
const FORM_ID = "rule-creation-form-id";


const RuleCreateDialog = ({ rule, isOpen, onClose, onValidateSuccess }) => {
  const { isRequestLoading, startLoading, stopLoading } = useRequestLoading();
  const [state, dispatch] = useContext(Context);
  const [currentStep, setCurrentStep] = useState(RuleFormSteps.CONFIGURATION); // CONFIGURATION, ACTION, SUMMARY, FINALISATION
  const [evaluatedRuleTargetResource, setEvaluatedRuleTargetResource] = useState();

  const handleSubmit = async (data) => {
    try {
      startLoading();
      await createRule(state.account.id, data, null);

      dispatch({
        type: "ALERT",
        alert: data.enabled ?
          { type: SUCCESS, msg: i18next.t("rules.notifications.activate", { name: `${data.name}`, interpolation: { escapeValue: false } }) }
          :
          { type: SUCCESS, msg: i18next.t("rules.notifications.register", { name: `${data.name}`, interpolation: { escapeValue: false } }) }
      });

      onValidateSuccess();
    } catch (error) {
      const err =
        error?.message?.messages?.error && error?.message?.messages?.error[0];
      dispatch({
        type: "ALERT",
        alert: { type: ERROR, msg: err?.code || "INTERNAL_ERROR" },
      });
    } finally {
      stopLoading();
    }
  };

  const getCustomDialogStyle = (step) => {
    const title = {
      marginBottom: "17px !important",
      fontSize: "25px",
      color: "#16161E",
      fontWeight: 600
    }

    return (
      step !== RuleFormSteps.FINALIZATION
        ? {
          backgroundColor: "#F2F3F7 !important",
          padding: "42px !important",
          "& h2": title
        }
        : {
          padding: "16px 28px !important",
          "& h2": title
        }
    )
  }

  const handleEvaluateRule = async (formData) => {
    try {
      startLoading();
      const response = await evaluateRule(state.account.id, formData, null);
      setEvaluatedRuleTargetResource(response.content);
    } catch (error) {
      const err =
        error?.message?.messages?.error && error?.message?.messages?.error[0];
      dispatch({
        type: "ALERT",
        alert: { type: ERROR, msg: err?.code || "INTERNAL_ERROR" },
      });
    } finally {
      stopLoading();
    }
  };

  return (
    <EADialog
      isOpen={isOpen}
      title={
        [RuleFormSteps.CONFIGURATION, RuleFormSteps.ACTION, RuleFormSteps.SUMMARY].includes(currentStep)
          ? i18next.t("rules.createDialog.configurationStepTitle")
          : i18next.t("rules.createDialog.finalizationStepTitle")
      }
      onCancel={() => {
        onClose();
      }}
      formId={FORM_ID}
      hasActions={false}
      fullWidth={true}
      maxWidth={[RuleFormSteps.CONFIGURATION, RuleFormSteps.ACTION, RuleFormSteps.SUMMARY].includes(currentStep) ? "popupxxl" : "popupsm"}
      customDialogStyle={getCustomDialogStyle(currentStep)}
      crossVisible={![RuleFormSteps.FINALIZATION].includes(currentStep)}
    >
      <RuleForm
        rule={rule}
        formId={FORM_ID}
        submitHandler={() => {/*Do nothing, the submit handler is passed to the final step*/ }}
      >
        <Box sx={{ display: [RuleFormSteps.CONFIGURATION, RuleFormSteps.ACTION, RuleFormSteps.SUMMARY].includes(currentStep) ? 'block' : 'none', position: "relative" }}>
          {/* <ConcernCounter count={5} type={"Tiers"} /> */}
          <Grid container spacing={"24px"}>
            <Grid item xs={12}>
              <RuleConfiguration
                open={currentStep === RuleFormSteps.CONFIGURATION}
                onCancel={() => {
                  onClose()
                }}
                onContinue={(data) => {
                  setCurrentStep(RuleFormSteps.ACTION)
                  handleEvaluateRule(data)
                }}
                onRuleTypeChange={()=> setCurrentStep(RuleFormSteps.CONFIGURATION)}
              />
            </Grid>

            <Grid item xs={12}>
              <RuleAction
                open={currentStep === RuleFormSteps.ACTION}
                onCancel={() => {
                  setCurrentStep(RuleFormSteps.CONFIGURATION)
                }}
                onContinue={() => {
                  setCurrentStep(RuleFormSteps.SUMMARY)
                }}
                disabled={currentStep === RuleFormSteps.CONFIGURATION}
              />
            </Grid>

            <Grid item xs={12}>
              <RuleSummary
                open={currentStep === RuleFormSteps.SUMMARY}
                onCancel={() => {
                  setCurrentStep(RuleFormSteps.ACTION)
                }}
                onContinue={() => {
                  setCurrentStep(RuleFormSteps.FINALIZATION)
                }}
                disabled={(
                  currentStep === RuleFormSteps.CONFIGURATION ||
                   currentStep === RuleFormSteps.ACTION
                )}
                evaluatedRuleTargetResource={evaluatedRuleTargetResource}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: [RuleFormSteps.FINALIZATION].includes(currentStep) ? 'block' : 'none' }}>
          <RuleFinalization
            open={currentStep === RuleFormSteps.FINALIZATION}
            onCancel={() => {
              setCurrentStep(RuleFormSteps.SUMMARY)
            }}
            onContinue={handleSubmit}
            isRequestLoading={isRequestLoading}
          />
        </Box>
      </RuleForm>
    </EADialog>
  );
};

export default RuleCreateDialog;
